import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useServerFn } from '@tanstack/react-start'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { getReviewByToken, submitReview } from '@/lib/reviews.functions'
import { resolveLocale, type Locale } from '@/lib/email-templates/i18n'

export const Route = createFileRoute('/review/$token')({
  head: () => ({
    meta: [
      { title: 'Laat een review achter — Casa Mi Pajobe' },
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),
  component: ReviewPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter()
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">Deze reviewlink is niet (meer) geldig</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <button
          onClick={() => {
            router.invalidate()
            reset()
          }}
          className="mt-6 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground"
        >
          Opnieuw proberen
        </button>
      </div>
    )
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-xl px-6 py-24 text-center text-muted-foreground">
      Reviewlink niet gevonden.
    </div>
  ),
})

type CopyEntry = {
  title: string
  intro: (name: string) => string
  stay: string
  ratingLabel: string
  ratingHint: string
  titleLabel: string
  titlePlaceholder: string
  commentLabel: string
  commentPlaceholder: string
  nameLabel: string
  namePlaceholder: string
  submit: string
  submitting: string
  thanks: string
  thanksBody: string
  alreadySubmitted: string
  update: string
  errorGeneric: string
  privacy: string
}

const T: Record<Locale, CopyEntry> = {
  nl: {
    title: 'Hoe was uw verblijf?',
    intro: (n) =>
      `${n ? `Beste ${n}, b` : 'B'}edankt om enkele minuten te nemen voor een review. Uw mening is heel waardevol voor ons en voor toekomstige gasten van Casa Mi Pajobe.`,
    stay: 'Verblijf',
    ratingLabel: 'Beoordeling',
    ratingHint: 'Klik op een ster (1 = zeer slecht, 5 = uitstekend)',
    titleLabel: 'Titel (optioneel)',
    titlePlaceholder: 'Een korte samenvatting van uw ervaring',
    commentLabel: 'Uw review',
    commentPlaceholder: 'Vertel ons wat u het mooist vond, wat we eventueel kunnen verbeteren…',
    nameLabel: 'Hoe mogen we u vermelden? (optioneel)',
    namePlaceholder: 'Bv. Jan & Marie uit Antwerpen',
    submit: 'Review versturen',
    submitting: 'Versturen…',
    thanks: 'Bedankt voor uw review!',
    thanksBody: 'We hebben uw beoordeling goed ontvangen — heel erg bedankt!',
    alreadySubmitted: 'U heeft eerder al een review ingediend. U kan deze hieronder bijwerken.',
    update: 'Review bijwerken',
    errorGeneric: 'Er ging iets mis. Probeer het opnieuw.',
    privacy:
      'Reviews worden enkel gepubliceerd na controle. We delen geen persoonlijke gegevens.',
  },
  fr: {
    title: 'Comment s’est passé votre séjour ?',
    intro: (n) =>
      `${n ? `Bonjour ${n}, m` : 'M'}erci de prendre quelques minutes pour laisser un avis. Votre opinion compte beaucoup pour nous et pour les futurs vacanciers de Casa Mi Pajobe.`,
    stay: 'Séjour',
    ratingLabel: 'Note',
    ratingHint: 'Cliquez sur une étoile (1 = très mauvais, 5 = excellent)',
    titleLabel: 'Titre (facultatif)',
    titlePlaceholder: 'Un court résumé de votre expérience',
    commentLabel: 'Votre avis',
    commentPlaceholder: 'Dites-nous ce que vous avez aimé, ce que nous pouvons améliorer…',
    nameLabel: 'Comment souhaitez-vous être mentionné ? (facultatif)',
    namePlaceholder: 'Ex. Jean & Marie de Lyon',
    submit: 'Envoyer l’avis',
    submitting: 'Envoi…',
    thanks: 'Merci pour votre avis !',
    thanksBody: 'Nous avons bien reçu votre évaluation — un grand merci !',
    alreadySubmitted: 'Vous avez déjà soumis un avis. Vous pouvez le mettre à jour ci-dessous.',
    update: 'Mettre à jour',
    errorGeneric: 'Une erreur est survenue. Veuillez réessayer.',
    privacy:
      'Les avis ne sont publiés qu’après vérification. Aucune donnée personnelle n’est partagée.',
  },
  en: {
    title: 'How was your stay?',
    intro: (n) =>
      `${n ? `Hi ${n}, t` : 'T'}hank you for taking a few minutes to share your feedback. It means a lot to us and to future guests of Casa Mi Pajobe.`,
    stay: 'Stay',
    ratingLabel: 'Rating',
    ratingHint: 'Click a star (1 = very poor, 5 = excellent)',
    titleLabel: 'Title (optional)',
    titlePlaceholder: 'A short summary of your experience',
    commentLabel: 'Your review',
    commentPlaceholder: 'Tell us what you loved and what we could improve…',
    nameLabel: 'How should we credit you? (optional)',
    namePlaceholder: 'e.g. John & Mary from London',
    submit: 'Submit review',
    submitting: 'Sending…',
    thanks: 'Thank you for your review!',
    thanksBody: 'We received your feedback — thank you so much!',
    alreadySubmitted: 'You have already submitted a review. You can update it below.',
    update: 'Update review',
    errorGeneric: 'Something went wrong. Please try again.',
    privacy: 'Reviews are only published after review. We never share personal data.',
  },
  de: {
    title: 'Wie war Ihr Aufenthalt?',
    intro: (n) =>
      `${n ? `Liebe(r) ${n}, v` : 'V'}ielen Dank, dass Sie sich ein paar Minuten Zeit nehmen, um eine Bewertung zu hinterlassen. Ihre Meinung ist sehr wertvoll für uns und für zukünftige Gäste von Casa Mi Pajobe.`,
    stay: 'Aufenthalt',
    ratingLabel: 'Bewertung',
    ratingHint: 'Klicken Sie auf einen Stern (1 = sehr schlecht, 5 = ausgezeichnet)',
    titleLabel: 'Titel (optional)',
    titlePlaceholder: 'Eine kurze Zusammenfassung Ihrer Erfahrung',
    commentLabel: 'Ihre Bewertung',
    commentPlaceholder: 'Erzählen Sie uns, was Ihnen gefallen hat und was wir verbessern können…',
    nameLabel: 'Wie sollen wir Sie nennen? (optional)',
    namePlaceholder: 'z. B. Jan & Marie aus Köln',
    submit: 'Bewertung senden',
    submitting: 'Wird gesendet…',
    thanks: 'Vielen Dank für Ihre Bewertung!',
    thanksBody: 'Wir haben Ihre Bewertung gut erhalten — vielen Dank!',
    alreadySubmitted: 'Sie haben bereits eine Bewertung abgegeben. Sie können sie unten aktualisieren.',
    update: 'Bewertung aktualisieren',
    errorGeneric: 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
    privacy:
      'Bewertungen werden erst nach Prüfung veröffentlicht. Persönliche Daten werden nicht weitergegeben.',
  },
}

function ReviewPage() {
  const { token } = Route.useParams()
  const fetchReview = useServerFn(getReviewByToken)
  const submitFn = useServerFn(submitReview)

  const query = useQuery({
    queryKey: ['review', token],
    queryFn: () => fetchReview({ data: { token } }),
    retry: false,
  })

  const detected: Locale = useMemo(() => {
    if (!query.data) return 'nl'
    return resolveLocale({
      preferred: query.data.preferredLanguage,
      country: query.data.country,
    })
  }, [query.data])
  const [chosen, setChosen] = useState<Locale | null>(null)
  const locale: Locale = chosen ?? detected
  const t = T[locale]

  const [rating, setRating] = useState(0)
  const [title, setTitle] = useState('')
  const [comment, setComment] = useState('')
  const [name, setName] = useState('')
  const [done, setDone] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (query.data?.existing) {
      setRating(query.data.existing.rating)
      setTitle(query.data.existing.title ?? '')
      setComment(query.data.existing.comment ?? '')
    }
    if (query.data?.guestName) setName(query.data.guestName)
  }, [query.data])

  const mutation = useMutation({
    mutationFn: () =>
      submitFn({
        data: { token, rating, title, comment, guestName: name, locale },
      }),
    onSuccess: () => {
      setDone(true)
      setErr(null)
    },
    onError: (e: any) => setErr(e?.message ?? t.errorGeneric),
  })

  if (query.isLoading) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center text-muted-foreground">
        Loading…
      </div>
    )
  }
  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">Deze reviewlink is niet (meer) geldig</h1>
      </div>
    )
  }

  const data = query.data
  const alreadySubmitted = !!data.submittedAt && !done

  if (done) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="text-3xl font-semibold">{t.thanks}</h1>
        <p className="mt-4 text-muted-foreground">{t.thanksBody}</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <header className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Casa Mi Pajobe · {data.reference}
          </p>
          <div className="flex gap-1">
            {(['nl', 'fr', 'en', 'de'] as Locale[]).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setChosen(l)}
                aria-pressed={locale === l}
                className={`rounded-full border px-3 py-1 text-xs uppercase ${
                  locale === l
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <h1 className="mt-2 text-3xl font-semibold">{t.title}</h1>
        <p className="mt-3 text-muted-foreground">{t.intro(data.guestName ?? '')}</p>
        <p className="mt-2 text-sm text-muted-foreground">
          {t.stay}: <strong>{data.checkIn} → {data.checkOut}</strong>
        </p>
        {alreadySubmitted ? (
          <p className="mt-4 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
            {t.alreadySubmitted}
          </p>
        ) : null}
      </header>

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault()
          if (rating < 1) {
            setErr(t.ratingHint)
            return
          }
          mutation.mutate()
        }}
      >
        <div>
          <label className="block text-sm font-medium">{t.ratingLabel}</label>
          <div className="mt-2 flex gap-1" role="radiogroup" aria-label={t.ratingLabel}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={rating === n}
                onClick={() => setRating(n)}
                className="text-3xl leading-none transition-transform hover:scale-110"
                style={{ color: n <= rating ? '#f4b400' : '#d4d4d4' }}
              >
                ★
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t.ratingHint}</p>
        </div>

        <div>
          <label className="block text-sm font-medium" htmlFor="r-title">
            {t.titleLabel}
          </label>
          <input
            id="r-title"
            type="text"
            maxLength={120}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t.titlePlaceholder}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium" htmlFor="r-comment">
            {t.commentLabel}
          </label>
          <textarea
            id="r-comment"
            rows={6}
            maxLength={4000}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t.commentPlaceholder}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium" htmlFor="r-name">
            {t.nameLabel}
          </label>
          <input
            id="r-name"
            type="text"
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.namePlaceholder}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        {err ? <p className="text-sm text-destructive">{err}</p> : null}

        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{t.privacy}</p>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {mutation.isPending ? t.submitting : alreadySubmitted ? t.update : t.submit}
          </button>
        </div>
      </form>
    </div>
  )
}